import { useAuth0 } from "@auth0/auth0-react";
import type { AuthContextValue, AuthUser } from "../types/auth.types";
import { AUTH_CONFIG } from "../config/auth.config";

/**
 * Unified authentication hook that provides consistent interface
 * regardless of whether Auth0 is enabled or disabled.
 *
 * When Auth0 is disabled, returns mock authenticated state.
 * When Auth0 is enabled, returns actual Auth0 state and methods.
 */
export function useAuth(): AuthContextValue {
  const auth0Enabled = AUTH_CONFIG.enabled;

  // Always call the hook to satisfy React's rules of hooks
  // When Auth0 is disabled, the Auth0Provider passes through, so this is safe
  const auth0 = useAuth0();

  // If Auth0 is disabled, return mock authenticated state
  if (!auth0Enabled) {
    return {
      isEnabled: false,
      isAuthenticated: true,
      isLoading: false,
      user: undefined,
      error: undefined,
      logout: () => {},
      loginWithRedirect: async () => {},
      getAccessTokenSilently: async () => "",
      getIdTokenClaims: async () => undefined,
    };
  }

  // Auth0 is enabled, use the actual hook values
  return {
    isEnabled: true,
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    user: auth0.user as AuthUser | undefined,
    error: auth0.error,
    logout: auth0.logout,
    loginWithRedirect: auth0.loginWithRedirect,
    getAccessTokenSilently: auth0.getAccessTokenSilently,
    getIdTokenClaims: auth0.getIdTokenClaims as () => Promise<Record<string, unknown> | undefined>,
  };
}
